import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient, PropertyResponse } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { NavigationHeader } from '@/components/ui/NavigationHeader'
import { getRenovationCosts, calculateMonthlyPayment, safeNumber } from '@/utils/propertyCalculations'
import { Home, TrendingUp, MapPin, BarChart3, Calculator, Briefcase, DollarSign, Target, ArrowUpRight, ArrowDownRight, Zap, Clock, Award } from 'lucide-react'

export function DashboardPage() {
  const { user } = useAuth()
  const [properties, setProperties] = useState<PropertyResponse[]>([])
  const [loading, setLoading] = useState(true)

  // Helper function to get user's first name for welcome message
  const getUserFirstName = () => {
    if (!user) return ''
    
    // If we have first name, use it
    if (user.given_name) {
      return user.given_name
    }
    
    // If we only have last name, use it
    if (user.family_name) {
      return user.family_name
    }
    
    // Fallback to email prefix
    if (user.email) {
      const emailPrefix = user.email.split('@')[0]
      return emailPrefix.split('.')[0] || emailPrefix
    }
    
    // Empty fallback for cleaner welcome message
    return ''
  }

  useEffect(() => {
    loadProperties()
  }, [])

  const loadProperties = async () => {
    try {
      console.log('🏠 Dashboard: Starting to load user properties...')
      console.log('📊 Dashboard: Current user:', user ? { email: user.email, sub: user.sub } : 'No user')

      const response = await apiClient.getUserProperties({ limit: 20 })

      console.log('✅ Dashboard: Successfully loaded properties response:', {
        propertiesCount: response.properties?.length || 0,
        totalCount: response.count,
        hasMore: response.has_more,
        properties: response.properties?.map(p => ({
          id: p.property_id,
          address: p.property_address,
          price: p.purchase_price,
          rent: p.expected_monthly_rent
        })) || []
      })

      setProperties(response.properties)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const errorStack = error instanceof Error ? error.stack : undefined

      console.error('❌ Dashboard: Failed to load properties:', {
        message: errorMessage,
        stack: errorStack,
        user: user ? { email: user.email, sub: user.sub } : 'No user'
      })

      // Additional error details for debugging
      if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
        console.error('🌐 Dashboard: Network connectivity issue detected')
      } else if (errorMessage.includes('401') || errorMessage.includes('Authentication')) {
        console.error('🔑 Dashboard: Authentication issue detected')
      } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        console.error('🚫 Dashboard: Authorization issue detected')
      }
    } finally {
      console.log('🏁 Dashboard: Property loading process completed')
      setLoading(false)
    }
  }

  // Calculate enhanced investment metrics
  const totalProperties = properties.length
  const totalInvestment = properties.reduce((sum, p) => {
    const purchasePrice = safeNumber(p.purchase_price)
    const renovationCosts = getRenovationCosts(p.renovation_costs)
    return sum + purchasePrice + renovationCosts
  }, 0)
  
  const totalCashFlow = properties.reduce((sum, p) => {
    // Debug logging for total cash flow calculation
    console.log('💰 Dashboard: Calculating cash flow for property:', {
      property_id: p.property_id,
      address: p.property_address,
      raw_data: {
        loan_amount: p.loan_amount,
        interest_rate: p.interest_rate,
        loan_term: p.loan_term,
        annual_taxes: p.annual_taxes,
        annual_insurance: p.annual_insurance,
        monthly_hoa_fees: p.monthly_hoa_fees,
        expected_monthly_rent: p.expected_monthly_rent
      }
    })

    const loanAmount = safeNumber(p.loan_amount)
    const interestRate = safeNumber(p.interest_rate)
    const loanTerm = safeNumber(p.loan_term, 30)

    console.log('🔧 Dashboard: Safe numbers for mortgage calculation:', {
      loanAmount,
      interestRate,
      loanTerm
    })

    const monthlyMortgage = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)

    console.log('🏠 Dashboard: Monthly mortgage payment:', monthlyMortgage)

    const monthlyTaxes = safeNumber(p.annual_taxes) / 12
    const monthlyInsurance = safeNumber(p.annual_insurance) / 12
    const monthlyHOA = safeNumber(p.monthly_hoa_fees)
    const expectedRent = safeNumber(p.expected_monthly_rent)

    console.log('📊 Dashboard: Monthly expense breakdown:', {
      monthlyMortgage,
      monthlyTaxes,
      monthlyInsurance,
      monthlyHOA,
      expectedRent
    })

    const monthlyExpenses = monthlyMortgage + monthlyTaxes + monthlyInsurance + monthlyHOA
    const monthlyCashFlow = expectedRent - monthlyExpenses

    console.log('💸 Dashboard: Cash flow calculation result:', {
      monthlyExpenses,
      monthlyCashFlow,
      isNaN_cashFlow: isNaN(monthlyCashFlow),
      current_sum: sum,
      new_sum: sum + monthlyCashFlow
    })

    // Check for NaN and handle it
    const safeCashFlow = isNaN(monthlyCashFlow) ? 0 : monthlyCashFlow
    return sum + safeCashFlow
  }, 0)

  const avgCashOnCashReturn = properties.length > 0 ? properties.reduce((sum, p) => {
    // Debug logging for cash-on-cash return calculation
    console.log('📈 Dashboard: Calculating cash-on-cash return for property:', {
      property_id: p.property_id,
      address: p.property_address,
      raw_data: {
        down_payment: p.down_payment,
        renovation_costs: p.renovation_costs,
        loan_amount: p.loan_amount,
        interest_rate: p.interest_rate,
        loan_term: p.loan_term
      }
    })

    const loanAmount = safeNumber(p.loan_amount)
    const interestRate = safeNumber(p.interest_rate)
    const loanTerm = safeNumber(p.loan_term, 30)
    const monthlyMortgage = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)

    const monthlyTaxes = safeNumber(p.annual_taxes) / 12
    const monthlyInsurance = safeNumber(p.annual_insurance) / 12
    const monthlyHOA = safeNumber(p.monthly_hoa_fees)
    const expectedRent = safeNumber(p.expected_monthly_rent)
    const monthlyExpenses = monthlyMortgage + monthlyTaxes + monthlyInsurance + monthlyHOA
    const monthlyCashFlow = expectedRent - monthlyExpenses

    const downPayment = safeNumber(p.down_payment)
    const renovationCosts = getRenovationCosts(p.renovation_costs)
    const cashInvested = downPayment + renovationCosts

    console.log('💵 Dashboard: Cash investment breakdown:', {
      downPayment,
      renovationCosts,
      cashInvested,
      monthlyCashFlow,
      annualCashFlow: monthlyCashFlow * 12
    })

    const cashOnCashReturn = cashInvested > 0 ? ((monthlyCashFlow * 12) / cashInvested) * 100 : 0

    console.log('🎯 Dashboard: Cash-on-cash return calculation:', {
      cashOnCashReturn,
      isNaN_return: isNaN(cashOnCashReturn),
      current_sum: sum,
      new_sum: sum + cashOnCashReturn
    })

    // Check for NaN and handle it
    const safeReturn = isNaN(cashOnCashReturn) ? 0 : cashOnCashReturn
    return sum + safeReturn
  }, 0) / properties.length : 0

  const totalPortfolioValue = properties.reduce((sum, p) => {
    const purchasePrice = safeNumber(p.purchase_price)
    const renovationCosts = getRenovationCosts(p.renovation_costs)
    return sum + purchasePrice + renovationCosts
  }, 0)

  const bestPerformingProperty = properties.length > 0 ? properties.reduce((best, current) => {
    const currentMortgage = calculateMonthlyPayment(
      safeNumber(current.loan_amount),
      safeNumber(current.interest_rate),
      safeNumber(current.loan_term, 30)
    )
    const currentExpenses = currentMortgage +
      (safeNumber(current.annual_taxes) / 12) +
      (safeNumber(current.annual_insurance) / 12) +
      safeNumber(current.monthly_hoa_fees)
    const currentCashFlow = safeNumber(current.expected_monthly_rent) - currentExpenses
    const currentInvestment = safeNumber(current.down_payment) + getRenovationCosts(current.renovation_costs)
    const currentReturn = currentInvestment > 0 ? ((currentCashFlow * 12) / currentInvestment) * 100 : 0

    if (!best) return current

    const bestMortgage = calculateMonthlyPayment(
      safeNumber(best.loan_amount),
      safeNumber(best.interest_rate),
      safeNumber(best.loan_term, 30)
    )
    const bestExpenses = bestMortgage +
      (safeNumber(best.annual_taxes) / 12) +
      (safeNumber(best.annual_insurance) / 12) +
      safeNumber(best.monthly_hoa_fees)
    const bestCashFlow = safeNumber(best.expected_monthly_rent) - bestExpenses
    const bestInvestment = safeNumber(best.down_payment) + getRenovationCosts(best.renovation_costs)
    const bestReturn = bestInvestment > 0 ? ((bestCashFlow * 12) / bestInvestment) * 100 : 0

    return currentReturn > bestReturn ? current : best
  }) : null

  const uniqueLocations = new Set(properties.map(p => `${p.city}, ${p.state}`).filter(Boolean)).size

  // Ensure final values are not NaN
  const safeTotalCashFlow = isNaN(totalCashFlow) ? 0 : totalCashFlow
  const safeAvgCashOnCashReturn = isNaN(avgCashOnCashReturn) ? 0 : avgCashOnCashReturn

  // Final debug log for calculated metrics
  console.log('🏆 Dashboard: Final calculated metrics:', {
    totalProperties,
    totalInvestment,
    totalCashFlow: safeTotalCashFlow,
    avgCashOnCashReturn: safeAvgCashOnCashReturn,
    totalPortfolioValue,
    uniqueLocations,
    original_totalCashFlow: totalCashFlow,
    original_avgCashOnCashReturn: avgCashOnCashReturn,
    wasNaN_totalCashFlow: isNaN(totalCashFlow),
    wasNaN_avgCashOnCashReturn: isNaN(avgCashOnCashReturn)
  })

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Welcome Section */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-primary-600 to-blue-700 rounded-lg p-8 text-white">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-bold mb-2">
                  Welcome back{getUserFirstName() ? `, ${getUserFirstName()}` : ''}!
                </h2>
                <p className="text-primary-100 text-lg mb-4">
                  Build wealth through strategic real estate investing. Analyze deals, track performance, and grow your portfolio with data-driven insights.
                </p>
                {totalProperties === 0 && (
                  <div className="flex items-center space-x-4">
                    <Link to="/analyze">
                      <Button className="bg-white text-primary-600 hover:bg-gray-100">
                        <Calculator className="w-4 h-4 mr-2" />
                        Analyze Your First Property
                      </Button>
                    </Link>
                    <span className="text-primary-200 text-sm">Start building your investment portfolio today</span>
                  </div>
                )}
                {totalProperties > 0 && (
                  <div className="flex items-center text-primary-100">
                    <Award className="w-5 h-5 mr-2" />
                    <span>Managing {totalProperties} investment {totalProperties === 1 ? 'property' : 'properties'}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-primary-200 text-sm">Portfolio Status</div>
                <div className="text-2xl font-bold">
                  {safeTotalCashFlow >= 0 ? 'Profitable' : 'Optimizing'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Investment Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Portfolio Value</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {loading ? '...' : `$${totalPortfolioValue.toLocaleString()}`}
                  </p>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Monthly Cash Flow</p>
                  <p className={`text-2xl font-semibold ${safeTotalCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {loading ? '...' : `$${safeTotalCashFlow.toLocaleString()}`}
                  </p>
                </div>
              </div>
              {safeTotalCashFlow >= 0 ? (
                <ArrowUpRight className="w-4 h-4 text-green-500" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-red-500" />
              )}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Target className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg Cash-on-Cash</p>
                  <p className={`text-2xl font-semibold ${safeAvgCashOnCashReturn >= 8 ? 'text-green-600' : safeAvgCashOnCashReturn >= 4 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {loading ? '...' : `${safeAvgCashOnCashReturn.toFixed(1)}%`}
                  </p>
                </div>
              </div>
              {safeAvgCashOnCashReturn >= 8 ? (
                <ArrowUpRight className="w-4 h-4 text-green-500" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-yellow-500" />
              )}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Home className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Properties</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {loading ? '...' : totalProperties}
                  </p>
                  <p className="text-xs text-gray-500">
                    {uniqueLocations} {uniqueLocations === 1 ? 'market' : 'markets'}
                  </p>
                </div>
              </div>
              <Zap className="w-4 h-4 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Investment Strategy Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Buy & Hold Strategy */}
          <div className="bg-white p-8 rounded-lg shadow-sm border hover:shadow-lg transition-shadow group">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Home className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Buy & Hold Analysis</h3>
              <p className="text-gray-600 mb-6">
                Build long-term wealth through rental properties. Calculate cash flow, cap rates, and cash-on-cash returns for sustainable passive income.
              </p>
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Ideal Cash-on-Cash:</span>
                  <span className="font-semibold text-green-600">8-12%+</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Investment Horizon:</span>
                  <span className="font-semibold text-blue-600">5-30+ years</span>
                </div>
              </div>
              <Link to="/analyze">
                <Button className="bg-blue-600 hover:bg-blue-700 w-full">
                  <Calculator className="w-4 h-4 mr-2" />
                  Analyze Buy & Hold
                </Button>
              </Link>
            </div>
          </div>

          {/* Fix & Flip Strategy */}
          <div className="bg-white p-8 rounded-lg shadow-sm border hover:shadow-lg transition-shadow group">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Fix & Flip Analysis</h3>
              <p className="text-gray-600 mb-6">
                Generate quick profits through strategic renovations. Calculate ARV, profit margins, and total project costs for maximum returns.
              </p>
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Target Profit Margin:</span>
                  <span className="font-semibold text-green-600">15-25%+</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Project Timeline:</span>
                  <span className="font-semibold text-green-600">3-12 months</span>
                </div>
              </div>
              <Link to="/analyze">
                <Button className="bg-green-600 hover:bg-green-700 w-full">
                  <Target className="w-4 h-4 mr-2" />
                  Analyze Fix & Flip
                </Button>
              </Link>
            </div>
          </div>

          {/* BRRR Strategy */}
          <div className="bg-white p-8 rounded-lg shadow-sm border hover:shadow-lg transition-shadow group">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">BRRR Strategy</h3>
              <p className="text-gray-600 mb-6">
                Buy, Rehab, Rent, Refinance - the ultimate wealth-building strategy. Recycle your capital for infinite returns and portfolio growth.
              </p>
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Capital Recovery:</span>
                  <span className="font-semibold text-purple-600">70-100%+</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Refinance LTV:</span>
                  <span className="font-semibold text-purple-600">75-80%</span>
                </div>
              </div>
              <Link to="/analyze">
                <Button className="bg-purple-600 hover:bg-purple-700 w-full">
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Analyze BRRR
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Portfolio Performance & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Portfolio Performance Overview */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Portfolio Performance</h3>
              <Link to="/portfolio" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View Details →
              </Link>
            </div>
            
            {totalProperties > 0 ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Best Performing Property</p>
                    <p className="font-semibold text-gray-900">
                      {bestPerformingProperty?.property_address || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Cash-on-Cash</p>
                    <p className="font-semibold text-green-600">
                      {bestPerformingProperty ? (() => {
                        const mortgage = calculateMonthlyPayment(
                          safeNumber(bestPerformingProperty.loan_amount),
                          safeNumber(bestPerformingProperty.interest_rate),
                          safeNumber(bestPerformingProperty.loan_term, 30)
                        )
                        const expenses = mortgage +
                          (safeNumber(bestPerformingProperty.annual_taxes) / 12) +
                          (safeNumber(bestPerformingProperty.annual_insurance) / 12) +
                          safeNumber(bestPerformingProperty.monthly_hoa_fees)
                        const cashFlow = safeNumber(bestPerformingProperty.expected_monthly_rent) - expenses
                        const investment = safeNumber(bestPerformingProperty.down_payment) + getRenovationCosts(bestPerformingProperty.renovation_costs)
                        const returns = investment > 0 ? ((cashFlow * 12) / investment) * 100 : 0
                        return `${returns.toFixed(1)}%`
                      })() : 'N/A'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Diversification</p>
                    <p className="text-lg font-semibold text-blue-800">
                      {uniqueLocations} {uniqueLocations === 1 ? 'Market' : 'Markets'}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Portfolio Health</p>
                    <p className="text-lg font-semibold text-green-800">
                      {safeTotalCashFlow >= 0 ? 'Positive' : 'Optimizing'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-4">No properties in your portfolio yet</p>
                <Link to="/analyze">
                  <Button size="sm">Add Your First Property</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Smart Recommendations & Quick Actions */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Smart Recommendations</h3>
            
            <div className="space-y-4">
              {totalProperties === 0 && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <Zap className="w-5 h-5 text-blue-500 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-blue-900">Get Started</p>
                      <p className="text-blue-700 text-sm mt-1">
                        Analyze your first property to unlock portfolio insights and performance tracking.
                      </p>
                      <Link to="/analyze" className="inline-block mt-2">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          Start Analysis
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {totalProperties > 0 && safeAvgCashOnCashReturn < 8 && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <Target className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-yellow-900">Optimize Returns</p>
                      <p className="text-yellow-800 text-sm mt-1">
                        Your average cash-on-cash return is {safeAvgCashOnCashReturn.toFixed(1)}%. Consider refinancing or finding higher-yield properties.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {totalProperties > 0 && safeTotalCashFlow < 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <ArrowDownRight className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-red-900">Negative Cash Flow</p>
                      <p className="text-red-700 text-sm mt-1">
                        Your portfolio has negative monthly cash flow. Review expenses and consider rent increases or cost reductions.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {totalProperties >= 3 && uniqueLocations === 1 && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-purple-500 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-purple-900">Diversify Markets</p>
                      <p className="text-purple-700 text-sm mt-1">
                        All properties are in one market. Consider diversifying across different locations to reduce risk.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {totalProperties > 0 && safeAvgCashOnCashReturn >= 12 && safeTotalCashFlow > 500 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start">
                    <Award className="w-5 h-5 text-green-500 mt-0.5 mr-3" />
                    <div>
                      <p className="font-medium text-green-900">Excellent Performance!</p>
                      <p className="text-green-700 text-sm mt-1">
                        Your portfolio is performing exceptionally well. Consider scaling with additional properties or exploring BRRR strategies.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="border-t pt-4 mt-6">
                <p className="font-medium text-gray-900 mb-3">Quick Actions</p>
                <div className="grid grid-cols-2 gap-3">
                  <Link to="/analyze">
                    <Button variant="secondary" size="sm" className="w-full justify-center">
                      <Calculator className="w-4 h-4 mr-2" />
                      New Analysis
                    </Button>
                  </Link>
                  <Link to="/portfolio">
                    <Button variant="secondary" size="sm" className="w-full justify-center">
                      <Briefcase className="w-4 h-4 mr-2" />
                      View Portfolio
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

